import { css, cx } from '@fable/theme'
import { Link, useLocation } from 'react-router-dom'
import { Genre } from '../../../../../types'
import { Color } from '@fable/legacy'
import { routes } from '../../../routes'
import { BREAKPOINTS } from '../../../../../constants'
import { applyImagePrefix } from '../../../../../utils'

interface Props {
  info: Genre
}

export const GenreMasthead: React.FC<Props> = ({ info }) => {
  const { pathname } = useLocation()
  const authorsLabel = `Our favorite ${info.name} authors`

  const renderAuthors = () => {
    const authors = info.authorsCollection.items

    return authors.map((author, index) => (
      <Link
        className="no-underline"
        to={`${routes.author}/${author.slug}`}
        state={{ prevPath: pathname }}
        key={index}
      >
        <div className={styles.author}>
          <div className={styles.authorAvatar}>
            <img
              src={`${applyImagePrefix(author.avatar.url)}?w=76`}
              alt={`${author.name} Avatar`}
            />
          </div>
          <p className={styles.authorName}>{author.name}</p>
        </div>
      </Link>
    ))
  }

  return (
    <section className={cx(styles.main)}>
      <div className={cx(styles.content, 'content')}>
        <div className={cx(styles.header)}>
          {info.logo && (
            <img
              className={cx(styles.logo)}
              src={info.logo.url}
              alt={`Fable ${info.name}`}
            />
          )}
          <p className={cx(styles.description)}>{info.description}</p>
        </div>
        <div className={cx(styles.authorsContainer)}>
          <p className={cx(styles.authorsLabel)}>{authorsLabel}</p>
          <div className={cx(styles.authors)}>{renderAuthors()}</div>
        </div>
      </div>
    </section>
  )
}

const styles = {
  main: css`
    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      max-width: 628px !important;
    }
  `,
  content: css`
    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      align-items: flex-start !important;
    }
  `,
  header: css`
    max-width: 380px;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      padding: 0 24px;
    }
  `,
  logo: css`
    margin: 24px 0 16px;
    max-width: 128px;
    display: block;

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      margin: 0 auto 30px;
      max-width: 217px;
    }
  `,
  description: css`
    font-size: 16px !important;
    line-height: 22px !important;
    font-weight: 600;
    letter-spacing: -0.2px !important;
    color: ${Color.whiteFang};

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      text-align: center;
      font-weight: 400;
    }
  `,
  authorsContainer: css`
    margin-top: 32px;
    width: 100%;

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      margin-top: 76px;
      width: auto;
    }

    @media only screen and (min-width: 960px) and (max-height: 780px) {
      margin-top: 30px;
    }
  `,
  authorsLabel: css`
    padding-left: 24px;
    font-size: 14px !important;
    line-height: 22px !important;
    color: ${Color.whiteFang};

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      padding-left: 0;
      text-align: center;
      font-size: 16px !important;
    }
  `,
  authors: css`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      padding: 0 24px;
      justify-content: flex-start;
      flex-wrap: nowrap;
    }
  `,
  author: css`
    margin-right: 3.466vw;
    margin-top: 25px;

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      margin-right: 30px;
    }
  `,
  authorAvatar: css`
    margin: 0 auto;
    display: block;
    background-color: ${Color.whiteSmoke};
    width: 12.8vw;
    height: 12.8vw;
    object-fit: cover;
    border-radius: 50%;
    position: relative;
    overflow: hidden;
    transition: box-shadow ease 0.2s;

    :hover {
      box-shadow: 0px 20px 33px rgba(0, 0, 0, 0.09),
        0px 15.0399px 13.7866px rgba(0, 0, 0, 0.0503198),
        0px 8.04107px 7.37098px rgba(0, 0, 0, 0.0417275),
        0px 4.50776px 4.13211px rgba(0, 0, 0, 0.035),
        0px 2.39404px 2.19453px rgba(0, 0, 0, 0.0282725);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    :after {
      content: '';
      width: calc(100% - 4px);
      height: calc(100% - 4px);
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border: 2px solid ${Color.whiteFang};
      border-radius: 50%;
      opacity: 0.25;
      z-index: 1;
    }

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      width: 76px;
      height: 76px;

      :after {
        width: calc(100% - 8px);
        height: calc(100% - 8px);
        border-width: 4px;
      }
    }
  `,
  authorName: css`
    margin-top: 8px;
    font-size: 2.66vw !important;
    line-height: 3.6vw !important;
    letter-spacing: 0.13vw;
    text-align: center;
    color: ${Color.whiteFang};
    max-width: 54px;
    text-align: center;
    white-space: break-spaces;

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      font-size: 14px !important;
      line-height: 18px !important;
      letter-spacing: 0.5px;
      max-width: 80px;
    }
  `,
}
